import React from "react";
import { fmtMonthDay } from "../../utils/dateFormatters";
import type {
  DepGraph,
  DepGraphActivity,
} from "../../../../models";
import type {
  ExternalCandidate,
} from "../DepChipTypeahead/DepChipTypeahead";
import type { SeveredRowInfo } from "../anchorLayout";
import ChipCell from "./ChipCell/ChipCell";
import InlineAddRow, { type InlineAddConfig } from "./InlineAddRow/InlineAddRow";
import EditActivityRow, { type ApplyEditArgs } from "./EditActivityRow/EditActivityRow";
import HeaderRow from "./HeaderRow/HeaderRow";
import PartnerRow from "./PartnerRow/PartnerRow";
import AnchorRow from "./AnchorRow/AnchorRow";
import ActivityRow from "./ActivityRow/ActivityRow";
import { buildCandidates } from "../candidates";
import type { DepEditIntents } from "./intents";
import {
  buildSuccAdjacency,
  buildZonesByCode,
  nodeStatus,
  predsFor,
  type RowLayoutEntry,
} from "../graphRows";
import "./DepTable.css";

export type { DepEditIntents } from "./intents";

interface Props {
  /** PROTOTYPE: activity id -> anchor direction; rows tied to outside /
   *  finished work wear an explicit ⚓ tag. */
  anchorTags?: Map<string, "in" | "out" | "both">;
  /** Activities around a detected dependency LOOP (rows flagged amber). */
  cycleNodes?: Set<string>;
  /** Loop ties as "predId>succId" (their chips flag red). */
  cycleEdges?: Set<string>;
  graph: DepGraph;
  rowLayout: RowLayoutEntry[];
  intents: DepEditIntents;
  selectedId: string | null;
  /** Direct predecessors / successors of the selected row — their rows tint
   *  light red / light yellow so the chain around the focus reads at a glance. */
  predIds?: Set<string>;
  succIds?: Set<string>;
  onSelect: (id: string) => void;
  /** Compact table pills (code + relationship only) — the pull-plan logic
   *  table. Defaults to the full chip (code + name) elsewhere. */
  compact?: boolean;
  /** Fixed-width columns that the covering gantt clips (the SequenceView logic
   *  editor). Off elsewhere (e.g. the takt-board dep editor), where the table
   *  flexes to fill its pane. */
  fixedWidth?: boolean;
  /** Duplicate an in-scope activity. Omitted -> the per-row control is hidden. */
  onDuplicate?: (activityId: string) => void;
  /** Fires whenever any row's rendered height changes (chip wrap, etc.). The
   *  strip uses this to keep bar/arrow positions aligned with the table. */
  onRowHeightsChange?: (heights: number[]) => void;

  // --- sequence-editing capabilities (pull-plan surface only; each one is
  // --- optional, so the takt-board modal renders the classic table) --------
  /** Inline duration editing (workdays) for in-scope, non-complete task rows. */
  onEditDuration?: (id: string, days: number) => void;
  /** Apply an edit-in-place (✎): code / name / days changes for ANY in-scope
   *  activity — committed or draft-added. */
  onApplyEdit?: (id: string, args: ApplyEditArgs) => void;
  /** Per-row delete (the host shows the external-tie guard before staging). */
  onDeleteActivity?: (id: string) => void;
  /** Whole-table read-only (browse mode): every chip cell renders read-only and
   *  the "+ add" affordances disappear, regardless of row scope. */
  readOnly?: boolean;
  /** Collapsed group keys — their header rows show a ▸ and their member rows
   *  are filtered out by the host (big-picture mode / focus one level). */
  collapsedGroups?: Set<string>;
  onToggleGroup?: (key: string) => void;
  /** ⊟/⊞ in the table header — collapse every level at once. */
  onToggleAllGroups?: () => void;
  allGroupsCollapsed?: boolean;
  /** ⧉ on a level's header — repeat that level onto other floors. */
  onRepeatLevel?: (zoneKey: string) => void;
  /** Anchors mode: outside ties become editable (add / retype / remove)
   *  instead of read-only context chips. */
  allowExternalEdits?: boolean;
  /** Query-driven out-of-section search for the "+ add" tie popover. */
  fetchExternals?: (query: string) => Promise<ExternalCandidate[]>;
  /** Anchors mode: marks each EXTERNAL row (by activity id) with every review
   *  key ("predId>succId") it covers — live outside ties plus ties severed by
   *  staged deletes. A marked row renders as outside/read-only context with
   *  ONE review checkbox that flips all of its keys. */
  anchorRowKeys?: Map<string, string[]>;
  /** Anchors mode: external id → its group. Drives the row's ←/→ arrow AND
   *  which side the single "In-section ties" column shows + edits: an incoming
   *  external ("in" — it feeds this section) shows its SUCCESSORS; an outgoing
   *  one ("out" — it waits on this section) shows its PREDECESSORS. The other
   *  side's chips don't render at all in anchors mode, so an add can never
   *  stage a tie in the wrong direction (which would flip the row's group). */
  anchorDirections?: Map<string, "in" | "out">;
  /** Anchors mode: external id → its severed-tie facts (ghost chips in the
   *  row's direction cell + the derived "⚠ tie severed" alarm). */
  severedInfo?: Map<string, SeveredRowInfo>;
  /** Anchors mode: reviewed tie keys + the per-key flip. */
  reviewedAnchors?: Set<string>;
  onToggleAnchorReview?: (key: string) => void;
  /** Inline "+ add activity" ghost rows (rendered for kind:"ghost" layout
   *  entries — the host opts into those via computeRowLayout's withAddRows). */
  inlineAdd?: InlineAddConfig;
  /** Open this ghost row (by its layout key) straight in edit mode. */
  autoEditGhostKey?: string | null;
  onAutoEditConsumed?: () => void;
  /** MS-Project-style insert: hover ＋ on a row opens an add row right below
   *  it. The host injects the transient ghost entry (key = insertGhostKey). */
  onInsertBelow?: (activityId: string) => void;
  insertGhostKey?: string | null;
  onInsertDismiss?: () => void;
  onInsertCommitted?: (draftId: string) => void;
  /** Bubbled per-ghost editing state (the host's forward-pass hold). */
  onGhostEditingChange?: (ghostKey: string, editing: boolean) => void;
}

const DepTable: React.FC<Props> = ({
  anchorTags,
  cycleNodes,
  cycleEdges,
  graph,
  rowLayout,
  intents,
  selectedId,
  predIds,
  succIds,
  onSelect,
  compact = false,
  fixedWidth = false,
  onDuplicate,
  onRowHeightsChange,
  onEditDuration,
  onApplyEdit,
  onDeleteActivity,
  readOnly: tableReadOnly = false,
  collapsedGroups,
  onToggleGroup,
  onToggleAllGroups,
  allGroupsCollapsed = false,
  onRepeatLevel,
  allowExternalEdits = false,
  fetchExternals,
  anchorRowKeys,
  anchorDirections,
  severedInfo,
  reviewedAnchors,
  onToggleAnchorReview,
  inlineAdd,
  autoEditGhostKey,
  onAutoEditConsumed,
  onInsertBelow,
  insertGhostKey,
  onInsertDismiss,
  onInsertCommitted,
  onGhostEditingChange,
}) => {
  const rowRefs = React.useRef(new Map<number, HTMLTableRowElement>());
  const lastReportedRef = React.useRef<number[]>([]);

  const measureAndReport = React.useCallback(() => {
    if (!onRowHeightsChange) return;
    const next: number[] = new Array(rowLayout.length).fill(0);
    for (let i = 0; i < rowLayout.length; i++) {
      const el = rowRefs.current.get(i);
      next[i] = el ? el.getBoundingClientRect().height : 0;
    }
    const prev = lastReportedRef.current;
    if (prev.length === next.length && prev.every((h, i) => h === next[i])) return;
    lastReportedRef.current = next;
    onRowHeightsChange(next);
  }, [rowLayout.length, onRowHeightsChange]);

  React.useLayoutEffect(() => {
    measureAndReport();
    const observer = new ResizeObserver(measureAndReport);
    rowRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [rowLayout, measureAndReport, graph]);

  const succAdjacency = React.useMemo(() => buildSuccAdjacency(graph), [graph]);

  const activities = React.useMemo(
    () => Object.values(graph.activities),
    [graph],
  );

  // Most-common year among the rows. A date in a DIFFERENT year gets a 2-digit
  // year suffix (e.g. 11/17/25) so a completed 2025 row reads unambiguously next
  // to 2026 work — matching the year markers on the gantt strip.
  const refYear = React.useMemo(() => {
    const counts = new Map<number, number>();
    for (const a of activities) {
      const iso = a.start || a.finish;
      if (!iso) continue;
      const y = Number(iso.slice(0, 4));
      counts.set(y, (counts.get(y) ?? 0) + 1);
    }
    let best = 0;
    let bestN = -1;
    for (const [y, n] of Array.from(counts)) {
      if (n > bestN) {
        best = y;
        bestN = n;
      }
    }
    return best;
  }, [activities]);

  const fmtDate = React.useCallback(
    (iso: string | null): string => {
      const base = fmtMonthDay(iso);
      if (!iso) return base;
      const y = Number(iso.slice(0, 4));
      return refYear && y !== refYear ? `${base}/${String(y).slice(2)}` : base;
    },
    [refYear],
  );

  // If the same work code appears in more than one zone, chips show the zone.
  const zonesByCode = React.useMemo(() => buildZonesByCode(activities), [activities]);

  // One pre-built, pre-sorted candidate list. Each row filters out itself.
  const allCandidates = React.useMemo(
    () => buildCandidates(activities, graph.link_candidates ?? []),
    [activities, graph.link_candidates],
  );

  // The leading select column survives only for the Anchors view's per-tie
  // review checkmark; the dependency table itself carries no checkboxes.
  const canSelect = Boolean(onToggleAnchorReview);
  // The trailing actions column hosts both the per-row delete and the
  // insert-below ＋; either capability brings the column in.
  const canDelete = Boolean(onDeleteActivity) || Boolean(onInsertBelow);
  // Anchors mode collapses Predecessors+Successors into ONE "In-section ties"
  // column: each external row only ever shows its direction-relevant side.
  const anchorsTable = anchorRowKeys != null;
  const totalCols =
    (anchorsTable ? 5 : 6) + (canSelect ? 1 : 0) + (canDelete ? 1 : 0);

  // In-scope, non-complete rows are the dependency-edit subjects (they get the
  // ✎ edit / ＋ insert / ✕ delete affordances; context/driver rows do not).
  const selectableId = (a: DepGraphActivity): boolean =>
    a.in_scope && nodeStatus(a) !== "complete";

  // One row is being edited in place at a time (✎ — code/name/days).
  const [editingRowId, setEditingRowId] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (editingRowId && !graph.activities[editingRowId]) setEditingRowId(null);
  }, [graph, editingRowId]);
  const canEditRows = Boolean(onApplyEdit && inlineAdd) && !tableReadOnly;

  if (activities.length === 0) {
    return (
      <div className="dep-table-empty">
        No activities in this section yet.
      </div>
    );
  }

  return (
    <table
      className={"dep-table" + (fixedWidth && !anchorsTable ? " dep-table--fixed" : "")}
    >
      <thead>
        <tr>
          {canSelect && <th className="dep-table-col-select" />}
          <th className="dep-table-col-name">
            {onToggleAllGroups && (
              <button
                type="button"
                className="dep-table-collapse-all"
                title={
                  allGroupsCollapsed
                    ? "Expand every level"
                    : "Collapse every level — read the plan floor by floor"
                }
                onClick={onToggleAllGroups}
              >
                {allGroupsCollapsed ? "⊞" : "⊟"}
              </button>
            )}
            Activity
          </th>
          <th
            className="dep-table-col-date"
            title="Retained (remaining) early start — where the schedule drives from. For in-progress work this is the remaining start, not the actual start (which shows as the shaded part of the bar)."
          >
            Start
          </th>
          <th
            className="dep-table-col-date"
            title="Retained (remaining) early finish — where the schedule drives from."
          >
            Finish
          </th>
          <th
            className="dep-table-col-dur"
            title="Duration in workdays."
          >
            Days
          </th>
          {anchorsTable ? (
            <th
              className="dep-table-col-chips"
              title="The section activities each outside row is tied to — an incoming row lists the in-section successors it feeds; an outgoing row the in-section preds it waits on."
            >
              In-section ties
            </th>
          ) : (
            <>
              <th className="dep-table-col-chips" title="What this activity comes after.">
                Preds
              </th>
              <th className="dep-table-col-chips" title="What follows this activity.">
                Succs
              </th>
            </>
          )}
          {canDelete && <th className="dep-table-col-actions" />}
        </tr>
      </thead>
      <tbody>
        {rowLayout.map((entry) => {
          const attachRowRef = (el: HTMLTableRowElement | null) => {
            if (el) rowRefs.current.set(entry.rowIndex, el);
            else rowRefs.current.delete(entry.rowIndex);
          };
          if (entry.kind === "header") {
            return (
              <HeaderRow
                key={entry.key}
                entry={entry}
                canSelect={canSelect}
                totalCols={totalCols}
                collapsedGroups={collapsedGroups}
                onToggleGroup={onToggleGroup}
                onRepeatLevel={onRepeatLevel}
                attachRowRef={attachRowRef}
              />
            );
          }
          if (entry.kind === "ghost") {
            if (!inlineAdd) {
              return <tr key={entry.key} ref={attachRowRef} className="dep-table-ghost-row" />;
            }
            const isInsert = insertGhostKey != null && entry.key === insertGhostKey;
            return (
              <InlineAddRow
                key={entry.key}
                entry={entry}
                graph={graph}
                config={inlineAdd}
                candidates={allCandidates}
                canSelect={canSelect}
                canDelete={canDelete}
                autoEdit={autoEditGhostKey === entry.key}
                onAutoEditConsumed={onAutoEditConsumed}
                onDismiss={isInsert ? onInsertDismiss : undefined}
                onCommitted={isInsert ? onInsertCommitted : undefined}
                onEditingChange={
                  onGhostEditingChange
                    ? (ed) => onGhostEditingChange(entry.key, ed)
                    : undefined
                }
                attachRowRef={attachRowRef}
              />
            );
          }
          const act = entry.activity;
          const isSelected = act.id === selectedId;

          // Anchors mode partner row — an in-section CONTEXT copy laid adjacent
          // to the external it's tied to, so the strip's arrow is one short
          // hop. The tie itself reads (and edits) from the external's row:
          // no chips, no checkbox, no actions here — just the clearly-marked
          // in-section activity at its dates.
          if (entry.partnerOf) {
            return (
              <PartnerRow
                key={entry.key}
                activity={act}
                partnerRole={entry.partnerRole}
                isSelected={isSelected}
                anchorsTable={anchorsTable}
                canSelect={canSelect}
                canDelete={canDelete}
                fmtDate={fmtDate}
                onSelect={onSelect}
                attachRowRef={attachRowRef}
              />
            );
          }

          const succs = succAdjacency.get(act.id) ?? [];

          // Anchor row — the anchors view's subject: an outside / finished
          // activity the section is tied to.
          const anchorKeys = anchorRowKeys?.get(act.id);
          if (anchorKeys) {
            return (
              <AnchorRow
                key={entry.key}
                activity={act}
                anchorKeys={anchorKeys}
                isSelected={isSelected}
                graph={graph}
                succs={succs}
                canSelect={canSelect}
                canDelete={canDelete}
                tableReadOnly={tableReadOnly}
                allowExternalEdits={allowExternalEdits}
                cycleEdges={cycleEdges}
                zonesByCode={zonesByCode}
                allCandidates={allCandidates}
                fetchExternals={fetchExternals}
                intents={intents}
                reviewedAnchors={reviewedAnchors}
                onToggleAnchorReview={onToggleAnchorReview}
                severedInfo={severedInfo}
                anchorDirections={anchorDirections}
                fmtDate={fmtDate}
                onSelect={onSelect}
                attachRowRef={attachRowRef}
              />
            );
          }

          // ✎ — the row swaps to the SAME editor the add row uses, prepopulated.
          if (canEditRows && editingRowId === act.id && inlineAdd) {
            const preds = predsFor(graph, act.id);
            return (
              <EditActivityRow
                key={entry.key}
                activity={act}
                scheduleId={inlineAdd.scheduleId}
                extraCodes={inlineAdd.extraCodes}
                canSelect={canSelect}
                predCell={
                  <ChipCell
                    cycleEdges={cycleEdges}
                    row={act}
                    side="pred"
                    preds={preds}
                    succs={succs}
                    readOnly={false}
                    zonesByCode={zonesByCode}
                    allCandidates={allCandidates}
                    fetchExternals={fetchExternals}
                    intents={intents}
                  />
                }
                succCell={
                  <ChipCell
                    cycleEdges={cycleEdges}
                    row={act}
                    side="succ"
                    preds={preds}
                    succs={succs}
                    readOnly={false}
                    zonesByCode={zonesByCode}
                    allCandidates={allCandidates}
                    fetchExternals={fetchExternals}
                    intents={intents}
                  />
                }
                onApply={(args) => {
                  onApplyEdit!(act.id, args);
                  setEditingRowId(null);
                }}
                onCancel={() => setEditingRowId(null)}
                onEditingChange={
                  onGhostEditingChange
                    ? (ed) => onGhostEditingChange(`edit-${act.id}`, ed)
                    : undefined
                }
                attachRowRef={attachRowRef}
              />
            );
          }

          const neighborRole: "pred" | "succ" | null = isSelected
            ? null
            : predIds?.has(act.id)
              ? "pred"
              : succIds?.has(act.id)
                ? "succ"
                : null;

          return (
            <ActivityRow
              key={entry.key}
              activity={act}
              predDrivers={entry.predDrivers}
              succDrivers={entry.succDrivers}
              isSelected={isSelected}
              neighborRole={neighborRole}
              graph={graph}
              succs={succs}
              anchorsTable={anchorsTable}
              compact={compact}
              canSelect={canSelect}
              canDelete={canDelete}
              tableReadOnly={tableReadOnly}
              allowExternalEdits={allowExternalEdits}
              canEditRows={canEditRows}
              cycleNodes={cycleNodes}
              cycleEdges={cycleEdges}
              zonesByCode={zonesByCode}
              allCandidates={allCandidates}
              fetchExternals={fetchExternals}
              intents={intents}
              anchorTags={anchorTags}
              onEditDuration={onEditDuration}
              onDuplicate={onDuplicate}
              onDeleteActivity={onDeleteActivity}
              onInsertBelow={onInsertBelow}
              selectableId={selectableId}
              fmtDate={fmtDate}
              onSelect={onSelect}
              onStartEdit={setEditingRowId}
              attachRowRef={attachRowRef}
            />
          );
        })}
      </tbody>
    </table>
  );
};

export default DepTable;
